// src/pages/misc/onboarding/usePriceBook.ts
// [PRICING] The book's load lifecycle, one hook for every page that prices (UNITS 3 to 7 of WU-142): one read of the server's price
//   book per mount (perSeatApi.fetchPerSeatPricing through the site's proxy), indexed once by priceBook.buildPriceBook and held here -
//   no page keeps a copy of the book. A refusal is THE FAILED READ (S-9): `failed` turns true and `book` stays null, so the page shows
//   PRICES_FAILED_LINE in place of its numbers and disables its gold button until `retry()` reads again and succeeds. The newest read
//   wins: a result that arrives after a later retry started, or after the page unmounted, is dropped.
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPerSeatPricing } from './perSeatApi';
import type { PriceBook } from './priceBook';
import { buildPriceBook } from './priceBook';

/** The one line every page shows when the book could not be read (S-9), in his words: never a number of the page's own. */
export const PRICES_FAILED_LINE = 'No pudimos cargar los precios. Intenta de nuevo.';

/** What a page reads from the hook. */
export interface PriceBookRead {
  /** The served book, indexed; null until a read succeeds. */
  book: PriceBook | null;
  /** True from a refusal until a read succeeds. */
  failed: boolean;
  /** Runs the read again. */
  retry: () => void;
}

interface Held {
  book: PriceBook | null;
  failed: boolean;
}

const UNREAD: Held = { book: null, failed: false };
const FAILED: Held = { book: null, failed: true };

export function usePriceBook(): PriceBookRead {
  const [held, setHeld] = useState<Held>(UNREAD);
  const reads = useRef(0);

  const read = useCallback(() => {
    reads.current += 1;
    const thisRead = reads.current;
    fetchPerSeatPricing()
      .then((result) => {
        if (thisRead !== reads.current) return;
        setHeld(result.ok ? { book: buildPriceBook(result.value), failed: false } : FAILED);
      })
      .catch((error: unknown) => {
        // fetchPerSeatPricing answers every failure as a refusal; only a book that could not be built from a served value lands here.
        if (thisRead !== reads.current) return;
        console.error('usePriceBook: the served price book could not be built', error);
        setHeld(FAILED);
      });
  }, []);

  useEffect(() => {
    read();
    return () => {
      reads.current += 1;
    };
  }, [read]);

  return { book: held.book, failed: held.failed, retry: read };
}
